import { useCallback, useMemo, useState } from 'react';

export interface ICommonUsedProperties {
    isPriceHeadLine?: boolean;
    isSectionHeadLine?: boolean;
    parentRowId?: string;
    rowId: string;
    name: string;
    number?: number;
    code?: string;
}

interface IUseUERBaseCostEstimateBasisRowsFiltered {
    items?: ICommonUsedProperties[];
}

type TFiltersItems = {
    codeFilterData: { label: string }[];
    nameFilterData: { label: string }[];
};

export const useUERBaseCostEstimateBasisRowsFiltered = (props: IUseUERBaseCostEstimateBasisRowsFiltered) => {
    const { items } = props;

    const [filterValues, setFilterValues] = useState<Record<string, string[]>>({});

    const filterData = useMemo(() => {
        const data =
            items?.reduce(
                (acc: TFiltersItems, item) => {
                    if (item.code && !acc.codeFilterData.find((i) => i.label === item.code)) {
                        acc.codeFilterData.push({ label: item.code });
                    }

                    if (item.name && !acc.nameFilterData.find((i) => i.label === item.name)) {
                        acc.nameFilterData.push({ label: item.name });
                    }

                    return acc;
                },
                {
                    codeFilterData: [],
                    nameFilterData: [],
                },
            ) || ({} as TFiltersItems);

        return {
            codeFilterData: data.codeFilterData.sort((a, b) => a.label?.localeCompare?.(b.label)),
            nameFilterData: data.nameFilterData.sort((a, b) => a.label?.localeCompare?.(b.label)),
        };
    }, [items]);

    const filteredData = useMemo(() => {
        if (!filterValues.code?.length && !filterValues.name?.length) {
            return items;
        }

        let idsToInclude = items?.filter((i) => i.isSectionHeadLine)?.map((i) => i.rowId) || [];

        items?.forEach((i) => {
            if (
                !idsToInclude?.includes(i.rowId) &&
                (!filterValues.code?.length || filterValues.code?.includes(i.code as string)) &&
                (!filterValues.name?.length || filterValues.name?.includes(i.name))
            ) {
                const childrenIds =
                    items
                        ?.filter((it) => !idsToInclude?.includes(it.rowId) && it.parentRowId === i.rowId)
                        ?.map((it) => it.rowId) || [];
                const parentId =
                    i.parentRowId && !idsToInclude?.includes(i.parentRowId as string) ? [i.parentRowId] : [];

                idsToInclude = [...idsToInclude, ...parentId, i.rowId, ...childrenIds];
            }
        });

        return items?.filter((i) => idsToInclude?.includes(i.rowId));
    }, [filterValues.code, filterValues.name, items]);

    const changeFilterHandler = useCallback(({ fieldName, checkedValues }) => {
        setFilterValues((prevValue) => ({
            ...prevValue,
            [fieldName]: checkedValues,
        }));
    }, []);

    return {
        changeFilterHandler,
        filterData,
        filteredData,
    };
};
